import { useState, useEffect, useCallback, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Send, Sparkles } from "lucide-react";
import {
  queryAcharya,
  fetchAcharyaSuggestions,
  loadAcharyaContext,
  logAcharyaError,
} from "@/lib/acharya/acharya-service";
import {
  sanitizeAcharyaAnswer,
  ACHARYA_INVALID_RESPONSE,
  hasAcharyaBody,
  hasStructuredSections,
  formatDisplayAnswer,
} from "@/lib/acharya/sanitizeAcharyaAnswer";
import { AcharyaStructuredBlocks } from "./AcharyaStructuredBlocks";
import { AcharyaAnswerBlocks } from "./AcharyaAnswerBlocks";
import { AcharyaAudioPlayer } from "./AcharyaAudioPlayer";
import { MantraAudioIcon } from "@/components/audio/MantraAudioIcon";
import { hasDevanagari, resolveMantraId } from "@/lib/audio/mantra-registry";

export function AcharyaChat({
  token,
  userId,
  userEmail,
  seekerState = "curious",
  onSeekerStateChange,
  labels = {},
}) {
  const L = {
    pageTitle: labels.pageTitle ?? "Āchārya AI",
    pageSubtitle: labels.pageSubtitle ?? "Śāstra-grounded guidance from Purāṇas, Itihāsa, Upaniṣads, Yoga, and Dharmaśāstra.",
    pageNote: labels.pageNote ?? "Responses are original summaries — not copyrighted quotations.",
    emptyTitle: labels.emptyTitle ?? "Ask your question",
    emptyBody: labels.emptyBody ?? "Your journey, tattva graph, and calendar inform each answer.",
    inputPlaceholder: labels.inputPlaceholder ?? "Ask about dharma, sādhana, or a story…",
    errorMessage: labels.errorMessage ?? "Āchārya is unavailable. Please try again.",
    invalidResponse: labels.invalidResponse ?? ACHARYA_INVALID_RESPONSE,
    seekerTone: labels.seekerTone ?? "Seeker tone (optional)",
    seekerStates: labels.seekerStates ?? [],
  };

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [context, setContext] = useState(null);
  const scrollRef = useRef(null);
  const bottomRef = useRef(null);
  const sessionIdRef = useRef(
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `web-${Date.now()}`
  );

  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading, scrollToBottom]);

  const refreshContext = useCallback(async () => {
    if (!token) {
      setContext(null);
      return;
    }
    const ctx = await loadAcharyaContext(token);
    setContext(ctx);
  }, [token]);

  useEffect(() => {
    refreshContext();
  }, [refreshContext]);

  useEffect(() => {
    fetchAcharyaSuggestions(token)
      .then(setSuggestions)
      .catch(() => setSuggestions([]));
  }, [token]);

  const ask = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;

    setMessages((prev) => [...prev, { role: "user", content: q }]);
    setInput("");
    setLoading(true);

    const assistantId = `a-${Date.now()}`;

    try {
      const ctx = context ?? (token ? await loadAcharyaContext(token) : null);
      // Capture history before placeholder — prior completed turns only (current q sent as `question`).
      const priorTurns = messages
        .filter((m) => !m.loading && (m.role === "user" || m.role === "assistant") && m.content?.trim())
        .map((m) => ({
          role: m.role,
          content:
            m.role === "assistant" && m.structured
              ? [
                  m.structured.reflection,
                  m.structured.teaching,
                  m.structured.closing,
                ]
                  .filter(Boolean)
                  .join(" ")
                  .trim() || m.content
              : m.content,
        }));

      setMessages((prev) => [
        ...prev,
        { id: assistantId, role: "assistant", content: "", structured: null, loading: true },
      ]);

      const raw = await queryAcharya(q, userId, token, ctx, {
        seekerState,
        sessionId: sessionIdRef.current,
        conversationHistory: priorTurns,
        flowMode: true,
      });
      const safe = sanitizeAcharyaAnswer(raw);

      if (
        safe.isUnavailable ||
        safe.error === "Invalid AI response" ||
        (!hasAcharyaBody(safe) && safe.error)
      ) {
        setMessages((prev) => {
          const withoutPlaceholder = prev.filter((m) => m.id !== assistantId);
          return [
            ...withoutPlaceholder,
            {
              role: "assistant",
              content: safe.answer?.trim() || L.invalidResponse,
              isError: true,
            },
          ];
        });
        setLoading(false);
        return;
      }

      const displayText = safe.answer?.trim() || "";

      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantId
            ? {
                ...m,
                content: displayText,
                structured: safe,
                loading: false,
              }
            : m
        )
      );
    } catch (err) {
      const friendly = await logAcharyaError(q, err, { userId, email: userEmail });
      setMessages((prev) => {
        const withoutPlaceholder = prev.filter((m) => m.id !== assistantId);
        return [
          ...withoutPlaceholder,
          { role: "assistant", content: friendly || L.errorMessage, isError: true },
        ];
      });
    }
    setLoading(false);
  };

  const seekerStateOptions = L.seekerStates?.length
    ? L.seekerStates
    : [
        { id: "curious", label: "Curious" },
        { id: "committed", label: "Committed" },
        { id: "hurting", label: "Hurting" },
      ];

  return (
    <div data-testid="acharya-chat">
      <div className="text-center mb-8">
        <h1 className="font-heading text-3xl md:text-4xl font-medium">{L.pageTitle}</h1>
        <p className="text-muted-foreground mt-2">{L.pageSubtitle}</p>
        <p className="text-xs text-muted-foreground/70 mt-1">{L.pageNote}</p>
        {context?.journey?.archetype && (
          <p className="text-xs text-primary mt-2">
            Context: {context.journey.archetype.name} · {context.journey.karmic_gate?.name} ·{" "}
            {context.journey.pathway?.name}
          </p>
        )}
        {onSeekerStateChange && (
          <div className="mt-4 flex flex-wrap justify-center gap-2 max-w-xl mx-auto">
            <span className="text-xs text-muted-foreground w-full text-center">{L.seekerTone}</span>
            {seekerStateOptions.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => onSeekerStateChange(s.id)}
                className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                  seekerState === s.id
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border/60 hover:border-primary/50"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <Card className="bg-white/90 backdrop-blur-sm shadow-float flex flex-col h-[min(85vh,720px)]">
        <div
          ref={scrollRef}
          className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-6 chat-scroll"
          data-testid="acharya-chat-scroll"
        >
          {messages.length === 0 ? (
            <div className="h-full min-h-[200px] flex flex-col items-center justify-center text-center py-12">
              <Sparkles className="w-16 h-16 text-muted-foreground/30 mb-4" />
              <h3 className="font-heading text-xl mb-2">{L.emptyTitle}</h3>
              <p className="text-muted-foreground text-sm mb-6 max-w-md">{L.emptyBody}</p>
              <div className="flex flex-wrap gap-2 justify-center max-w-lg">
                {(suggestions ?? []).map((s) => (
                  <Button
                    key={s.id}
                    variant="outline"
                    size="sm"
                    className="text-xs"
                    onClick={() => ask(s.question)}
                  >
                    {s.question}
                  </Button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-6 pb-2">
              {messages.map((msg, i) => {
                const safe = msg.structured ? sanitizeAcharyaAnswer(msg.structured) : null;
                const audioText = msg.content || safe?.answer || "";
                const isFlow = safe?.response_mode === "flow";
                return (
                  <div key={msg.id ?? i}>
                    <div
                      className={`chat-message ${msg.role} ${msg.isError ? "opacity-90" : ""}`}
                      data-testid={`acharya-message-${i}`}
                    >
                      {msg.loading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        msg.content?.trim() &&
                          !(
                            msg.role === "assistant" &&
                            safe &&
                            hasStructuredSections(safe)
                          ) && <p className="whitespace-pre-wrap">{msg.content}</p>
                      )}
                    </div>
                    {safe && msg.role === "assistant" && !msg.isError && (
                      <>
                        {!isFlow && hasStructuredSections(safe) && <AcharyaAnswerBlocks data={safe} />}
                        {!isFlow && <AcharyaStructuredBlocks data={safe} token={token} anonymous={!token} />}
                      </>
                    )}
                    {msg.role === "assistant" && !msg.loading && !msg.isError && (
                      <div className="mt-2">
                        {msg.audio_url ? (
                          <AcharyaAudioPlayer src={msg.audio_url} token={token} />
                        ) : (
                          <AcharyaAudioPlayer
                            mode="voice"
                            text={
                              safe?.reflection?.trim() ||
                              safe?.teaching?.trim() ||
                              audioText
                            }
                            token={token}
                          />
                        )}
                      </div>
                    )}
                    {msg.role === "assistant" && hasDevanagari(audioText) && (
                      <div className="mt-2" data-testid="acharya-mantra-icon-inline">
                        <MantraAudioIcon
                          mantra={resolveMantraId(audioText) ?? audioText}
                          token={token}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
              {loading && messages.every((m) => !m.loading) && (
                <div className="chat-message assistant">
                  <Loader2 className="w-5 h-5 animate-spin" />
                </div>
              )}
              <div ref={bottomRef} aria-hidden="true" />
            </div>
          )}
        </div>

        <div className="shrink-0 p-4 border-t border-border/60 bg-white/90">
          <div className="flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && ask()}
              placeholder={L.inputPlaceholder}
              className="flex-1 bg-white/50"
              disabled={loading}
              data-testid="acharya-chat-input"
            />
            <Button onClick={() => ask()} disabled={loading || !input.trim()} data-testid="acharya-chat-send">
              <Send className="w-4 h-4" />
            </Button>
          </div>

          <div className="flex flex-wrap gap-2 mt-3">
            <AcharyaAudioPlayer mode="meditation" label="Start Meditation" text="Breath meditation" token={token} />
            <MantraAudioIcon mantra="om" token={token} />
          </div>
        </div>
      </Card>
    </div>
  );
}
